import { useState } from 'react';
import { Alert, Badge, Button, Card, Code, Group, PinInput, Stack, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Actor } from '@prooffleet/contracts';
import { ApiError, api, useSession } from '@prooffleet/client';
import { PageHeader } from '../components/Page.js';

interface MfaSetup {
  secret: string;
  otpauthUrl: string;
}

/**
 * Второй фактор для собственной учётной записи.
 *
 * Роли с доступом к порталу обязаны иметь TOTP: менеджер видит весь флот, и
 * это лакомая цель. Без этого экрана включить фактор было невозможно — вход
 * возвращал требование настроить его, а настроить было негде.
 */
export function Security(): React.JSX.Element {
  const actor = useSession((s) => s.actor);
  const queryClient = useQueryClient();

  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const { data: me } = useQuery({
    queryKey: ['me', 'security'],
    queryFn: () => api<{ actor: Actor; mfaEnabled?: boolean }>('/auth/me'),
  });

  const begin = useMutation({
    mutationFn: () => api<MfaSetup>('/auth/mfa/setup', { method: 'POST' }),
    onSuccess: (result) => {
      setSetup(result);
      setError(null);
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Could not start enrollment');
    },
  });

  const enable = useMutation({
    mutationFn: () => api('/auth/mfa/enable', { method: 'POST', body: { token: code } }),
    onSuccess: async () => {
      setDone(true);
      setSetup(null);
      setCode('');
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Wrong code');
    },
  });

  const enabled = done || me?.mfaEnabled === true;

  return (
    <Stack gap={0}>
      <PageHeader
        title="Security"
        hint="Your own account. Roles with portal access are required to carry a second factor, because one stolen password would open the whole fleet."
      />

      <Card maw={560}>
        <Group justify="space-between" align="center" mb="sm">
          <div>
            <Text fw={600} fz="sm">
              Two-factor authentication
            </Text>
            <Text c="dimmed" fz="sm">
              {actor?.roleKey.replace('_', ' ')}
            </Text>
          </div>
          {enabled ? (
            <Badge color="gray">enabled</Badge>
          ) : (
            <Badge color="red.8" variant="filled">
              not set up
            </Badge>
          )}
        </Group>

        {!enabled && setup === null && (
          <Stack gap="sm">
            <Text fz="sm" c="gray.7">
              You will need an authenticator app such as 1Password, Google Authenticator, or Authy.
            </Text>
            <Button loading={begin.isPending} onClick={() => begin.mutate()}>
              Set up
            </Button>
          </Stack>
        )}

        {setup !== null && (
          <Stack gap="sm">
            <Text fz="sm">
              Add this secret to your authenticator app, then enter the six digits it shows.
            </Text>
            <Code block>{setup.secret}</Code>
            <Text fz="xs" c="dimmed">
              Apps that accept a URI can take this one instead:
            </Text>
            <Code block style={{ wordBreak: 'break-all' }}>
              {setup.otpauthUrl}
            </Code>

            <PinInput length={6} type="number" inputMode="numeric" value={code} onChange={setCode} />

            <Button
              loading={enable.isPending}
              disabled={code.length !== 6}
              onClick={() => enable.mutate()}
            >
              Confirm and enable
            </Button>
          </Stack>
        )}

        {done && (
          <Alert color="blue" mt="sm">
            Two-factor authentication is on. You will be asked for a code at every sign-in.
          </Alert>
        )}

        {error !== null && (
          <Alert color="red" mt="sm">
            {error}
          </Alert>
        )}
      </Card>
    </Stack>
  );
}
